import React, { useEffect, useState } from "react";
import { Modal, Form, Spin, Button, message, Tooltip } from "antd";
import { useParams } from "react-router-dom";
import { yearCalculator } from "../../../../../utils/ageCalculator";
import MedicalCardsTitle from "../MedicalCardsTitle";
import { CopyOutlined } from "@ant-design/icons";
import CurrentMedicationModal from "../../Prescription/component/CurrentMedicationModal";
import { useStateGlobalContext } from "../../../../../contexts/GlobalContext";
import FormatDate from "../../../../../components/FormatDate/FormatDate";
import FormatDateTime from "../../../../../components/FormatDate/FormatDateTime";
import LabelDisplay from "../../../../../components/LabelDisplay/LabelDisplay";
import { AddButton } from "../../../../../components/GlobalComponents/GlobalComponents";
import dayjs from "dayjs";

function CurrentMedications({showCopyAll}) {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [viewAll, setViewAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentMedicationView, setCurrentMedicationView] = useState(false);
  const [currentMedicationModal, setCurrentMedicationModal] = useState(false);
  const { currentMedication: data, getCurrentMedications, officeSetting } =
    useStateGlobalContext();
  const timezone = officeSetting?.facility_time_zone || 'UTC';

  const handleCurrentMedicationModal = () => {
    setCurrentMedicationModal(true);
  };

  const hideCurrentMedcicationModal = () => {
    setCurrentMedicationModal(false);
  };

  const handleCancel = () => {
    form.resetFields();
    setCurrentMedicationView(false);
  };

  const handleCurrentMedicationView = (record) => {
    setSelectedItem(record);
    setCurrentMedicationView(true);
  };

  const handleCopy = (item) => {
    const textToCopy = `
      Medicine Name: ${item?.medicine_name || "N/A"}
      Start Date: ${item?.start_date || "N/A"}
      End Date: ${item?.end_date || "N/A"}
    `;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        message.success("Copied to clipboard");
      })
      .catch((err) => {
        message.error("Failed to copy to clipboard");
      });
  };

  const handleCopyAll = () => {
    let textToCopy = "PATIENT CURRENT MEDICATIONS\n";
    textToCopy += "=================\n\n";
    textToCopy += data.map(item => `Medicine Name: ${item?.medicine_name || "N/A"}\nStart Date: ${item?.start_date || "N/A"}\nEnd Date: ${item?.end_date || "N/A"}\nNotes: ${item?.notes || "N/A"}\nAdded By: ${item?.added_by || "N/A"}\nAdded Date: ${item?.date_time ? dayjs(item?.date_time).format("YYYY-MM-DD") : "N/A"} `).join("\n\n");
    navigator.clipboard.writeText(textToCopy).then(() => {
      message.success("Copied to clipboard");
    }).catch(() => {
      message.error("Failed to copy to clipboard"); 
    }); 
  }

  useEffect(() => {
    getCurrentMedications(id);
  }, [id]);

  return (
    <div>
      <div className="shadow-class p-4 border rounded-md  h-[180px]  max-h-[180px]">
        <div className="flex justify-between items-center">
          <MedicalCardsTitle title="All Current Medications" />
          <div className="flex items-center gap-2">
            {showCopyAll && data && data.length > 0 && (
              <Tooltip title="Copy All">
                <CopyOutlined
                  className="text-[#1890ff] mr-2"
                  onClick={handleCopyAll}
                />
              </Tooltip>
            )}
            <AddButton openModal={handleCurrentMedicationModal}/>
          </div>
        </div>

        <CurrentMedicationModal
          open={currentMedicationModal}
          onCancel={hideCurrentMedcicationModal}
        />

        {loading ? (
          <div className="flex justify-center items-center">
            <Spin size="small" />
          </div>
        ) : (
          <div className="max-h-[130px]  overflow-auto mt-1">
            {data
              ?.filter((item) => {
                return !item.end_date || new Date(item.end_date) >= new Date();
              })
              .map((item, idx) => (
                <div key={idx} className="mb-2">
                  <div className="flex gap-2">
                  <p>
                    <span
                      className="text-[#1890ff] cursor-pointer"
                      onClick={() => handleCurrentMedicationView(item)}
                    >
                      {item?.medicine_name?.slice(0, 50)}{" "}
                      {item?.end_date
                        ? ` (${yearCalculator(
                            item?.start_date,
                            item?.end_date,
                            timezone
                          )} years)`
                        : " (present)"}
                    </span>
                    <span
                      className={
                        item?.end_date && new Date(item?.end_date) < new Date()
                          ? "text-white bg-slate-500 text-xs rounded-md px-1 py-[2px] ml-2"
                          : "text-white bg-[#3484F0] text-xs rounded-md px-1 py-[2px] ml-2"
                      }
                    >
                      {item?.end_date && new Date(item?.end_date) < new Date()
                        ? "Inactive"
                        : "Active"}
                    </span>
                  </p>
                  <CopyOutlined
                    className="text-[#1890ff] mr-2"
                    onClick={() => {
                      handleCopy(item);
                    }}
                  />
                  </div>
                  <p>
                    <span className="font-medium">Start Date:</span>
                    <span>
                      {" "}
                      {item?.start_date ? (
                        <FormatDate date={item?.start_date} />
                      ) : (
                        "No Date Entered"
                      )}
                    </span>
                    <span className="font-medium ml-3 ">End Date: </span>
                    <span>
                      {item?.end_date ? (
                        <FormatDate date={item?.end_date} />
                      ) : (
                        "No Date Entered"
                      )}
                    </span>
                  </p>
                </div>
              ))}

            {data?.length === 0 && (
              <li className="flex justify-between items-center">
                <span>No Medications Added</span>
              </li>
            )}
          </div>
        )}
      </div>

      <Modal
        title="Current Medication Details"
        open={currentMedicationView}
        footer={null}
        closeIcon={
          <Button size="small" className="app-close-icon" shape="round">
            Close
          </Button>
        }
        onCancel={handleCancel}
      >
        <LabelDisplay
          title="Medicine Name"
          description={selectedItem?.medicine_name}
        />
        <LabelDisplay
          title="Start Date"
          description={
            selectedItem?.start_date ? (
              <FormatDate date={selectedItem?.start_date} />
            ) : (
              "No Date Entered"
            )
          }
        />

        <LabelDisplay
          title="End Date"
          description={
            selectedItem?.end_date ? (
              <FormatDate date={selectedItem?.end_date} />
            ) : (
              "No Date Entered"
            )
          }
        />
        <LabelDisplay title="Notes" description={selectedItem?.notes} />
        <LabelDisplay title="Added By" description={selectedItem?.added_by} />
        <LabelDisplay
          title="Added Date"
          description={
            <FormatDateTime date={selectedItem?.date_time} type="datetime" />
          }
        />
      </Modal>

      <Modal
        title="All Medications"
        open={viewAll}
        footer={null}
        maskClosable={false}
        onCancel={() => setViewAll(false)}
      >
        {!loading &&
          data &&
          data?.map((item, index) => (
            <div key={index}>
              <p>
                <span
                  className="text-[#1890ff] cursor-pointer"
                  onClick={() => handleCurrentMedicationView(item)}
                >
                  {item?.medicine_name}{" "}
                  {item?.end_date
                    ? ` (${yearCalculator(
                        item?.start_date,
                        item?.end_date
                      )} years)`
                    : " (present)"}
                </span>
                <span
                  className={
                    item?.end_date && new Date(item?.end_date) < new Date()
                      ? "text-white bg-slate-500 text-xs rounded-md px-1 py-[2px] ml-2"
                      : "text-white bg-[#3484F0] text-xs rounded-md px-1 py-[2px] ml-2"
                  }
                >
                  {item?.end_date && new Date(item?.end_date) < new Date()
                    ? "Inactive"
                    : "Active"}
                </span>
              </p>
              <p>
                <span className="font-medium">Start Date: </span>
                <span>
                  {" "}
                  <FormatDate date={item?.start_date} />
                </span>
                <span className="font-medium ml-3 ">End Date: </span>
                <span>
                  {item?.end_date ? (
                    <FormatDate date={item?.end_date} />
                  ) : (
                    "No Date Entered"
                  )}
                </span>
              </p>
            </div>
          ))}
        {data?.length === 0 && (
          <li className="flex justify-between items-center">
            <span>No Medications Added</span>
          </li>
        )}
      </Modal>
    </div>
  );
}

export default CurrentMedications;
